import { v4 as uuid } from "uuid";

import { _ } from "js/vendor";
import * as geom from "js/core/utilities/geom";
import { TextStyleType, BlockStructureType, NodeType } from "common/constants";
import { Shape } from "js/core/utilities/shapes";

import { BaseElement } from "../base/BaseElement";
import { TextElement } from "../base/Text/TextElement";
import { SVGPolylineElement } from "../base/SVGElement";
import { AnnotationLayer } from "./AnnotationLayer";

class TimelineAnnotations extends AnnotationLayer {
    get maxItemCount() {
        return 11;
    }

    get canEditNumberedTextContentItemMarkerValue() {
        return false;
    }

    getAllowedNodeTypes() {
        return [];
    }

    getChildOptions(model) {
        const sortedItems = _.cloneDeep(this.model.items).sort((a, b) => a.x - b.x).map(({ x, id }, index) => ({ x, id, index }));

        return {
            ...super.getChildOptions(model),
            canChangeTextDirection: false,
            allowTextAlignment: false,
            horizontalScaleOrigin: "left",
            textAlign: "left",
            itemIndex: sortedItems.find(({ id }) => id === model.id).index,
            allowedTextBlockTypes: [TextStyleType.TITLE, TextStyleType.BODY],
            allowedFrameCategories: ["shape"],
            showResizeSlider: false,
            syncFontSizeWithSiblings: true,
            renderBackdrop: true,
            getRegistrationPoint: model.nodeType === NodeType.TEXT
                ? element => new geom.Point(-element.text.calculatedProps.textBounds.left - element.anchorPadding, 0)
                : null,
            getAnchorPoint: model.nodeType === NodeType.TEXT
                ? (element, connector, anchor) => {
                    const anchorBounds = element.text.calculatedProps.textBounds.offset(element.bounds.position);
                    return new geom.Point(anchorBounds.left - element.anchorPadding, model.y > 0.5 ? anchorBounds.bottom : anchorBounds.top);
                }
                : null
        };
    }

    getConnectorGroupOptions() {
        return {
            ...super.getConnectorGroupOptions(),
            getConnectorOptions: () => ({
                canSelect: false,
                convertToAuthoringAsSVG: true
            })
        };
    }

    get constrainNodesToBounds() {
        return false;
    }

    getAnimations() {
        const sortedItems = this.itemElements
            .map(({ id, itemIndex }) => ({ id, itemIndex }))
            .sort((a, b) => a.itemIndex - b.itemIndex)
            .map(({ id }) => this.elements[id]);

        return sortedItems.map(node => {
            const nodeAnimations = node.getAnimations();

            node.connectorsFromNode.forEach(connector => {
                const connectorAnimations = connector.getAnimations();
                nodeAnimations.push(...connectorAnimations);
            });

            // Combine node and connectors animations into one
            const animation = {
                ...nodeAnimations[0]
            };

            animation.animatingElements = nodeAnimations.map(({ element }) => element);
            animation.prepare = () => nodeAnimations
                .filter(({ prepare }) => !!prepare)
                .forEach(({ prepare }) => prepare());
            animation.onBeforeAnimationFrame = progress => {
                nodeAnimations
                    .filter(({ onBeforeAnimationFrame }) => !!onBeforeAnimationFrame)
                    .forEach(({ onBeforeAnimationFrame }) => onBeforeAnimationFrame(progress));
                // Letting the canvas know it should refresh annotations layer on every frame
                return this;
            };
            animation.finalize = () => nodeAnimations
                .filter(({ finalize }) => !!finalize)
                .forEach(({ finalize }) => finalize());

            return animation;
        });
    }
}

export class Timeline extends BaseElement {
    get canRefreshElement() {
        return true;
    }

    refreshElement(transition) {
        this.canvas.refreshElement(this, transition);
    }

    get canRollover() {
        return true;
    }

    getCanvasMargins() {
        return {
            left: this.model.showStartMarker ? 50 : 0,
            right: this.model.showEndMarker ? 50 : 0,
            top: 50,
            bottom: 50
        };
    }

    getDefaultAnnotationModel() {
        return {
            id: uuid(),
            nodeType: this.model.annotations?.items[0]?.nodeType ?? NodeType.TEXT,
            hasBeenDragged: false,
            x: Math.random(),
            y: this.model.annotations ? (this.model.annotations.items.length % 2 === 0 ? 0.25 : 0.75) : 0.25,
            hideNodeConnectorWidget: true,
            textDirection: "right"
        };
    }

    getAnnotationConnectorModel(annotationId, startAnchor) {
        return {
            id: `from-${annotationId}`,
            source: annotationId,
            target: `${this.uniquePath}/timeline`,
            endPointIsLocked: true,
            canChangeConnectorType: false,
            endDecoration: this.drawConnectorDecoration,
            startAnchor
        };
    }

    drawConnectorDecoration(pt) {
        let r;
        switch (this.canvas.getTheme().get("styleWeight")) {
            case "light":
                r = 5;
                break;
            case "medium":
                r = 8;
                break;
            case "heavy":
                r = 10;
                break;
        }

        return {
            trimAmount: 0,
            path: Shape.drawCircle(r, pt).toPathData()
        };
    }

    _build() {
        if (this.model.showStartMarker) {
            this.startMarker = this.addElement("start_marker", () => TimelineMarker, {
                model: this.model.startMarker,
                scaleTextToFit: true,
                placeholder: "Type text",
                blockStructure: BlockStructureType.SINGLE_BLOCK,
                defaultBlockTextStyle: TextStyleType.BODY,
                matchTextScaleId: "marker",
                syncFontSizeWithSiblings: !!this.model.showEndMarker,
                getSiblings: () => [this.startMarker, this.endMarker]
            });
        }
        if (this.model.showEndMarker) {
            this.endMarker = this.addElement("end_marker", () => TimelineMarker, {
                model: this.model.endMarker,
                scaleTextToFit: true,
                placeholder: "Type text",
                blockStructure: BlockStructureType.SINGLE_BLOCK,
                defaultBlockTextStyle: TextStyleType.BODY,
                matchTextScaleId: "marker",
                syncFontSizeWithSiblings: !!this.model.showStartMarker,
                getSiblings: () => [this.startMarker, this.endMarker]
            });
        }

        this.timeline = this.addElement("timeline", () => TimelineSpans);

        if (!this.model.annotations) {
            this.model.annotations = { items: [] };
        }
        if (!this.model.annotations.connections) {
            this.model.annotations.connections = { items: [] };
        }

        // Generate connectors
        this.model.annotations.connections.items = this.model.annotations.items
            .map(milestoneModel =>
                this.getAnnotationConnectorModel(milestoneModel.id, milestoneModel.nodeType === NodeType.TEXT ? geom.AnchorType.LEFT : geom.AnchorType.FREE)
            );

        this.annotations = this.addElement("annotations", () => TimelineAnnotations, { model: this.model.annotations });
        this.annotations.layer = 2;
    }

    _calcProps(props, options) {
        const { size } = props;

        // Timeline bounds start at full bounds and then are reduced if showing start/end markers
        let timelineBounds = new geom.Rect(0, 0, size);

        if (this.model.showStartMarker) {
            const startMarker = this.startMarker.calcProps(new geom.Size(this.styles.markerSize, this.styles.markerSize));
            startMarker.bounds = new geom.Rect(0, size.height / 2 - this.styles.markerSize / 2, this.styles.markerSize, this.styles.markerSize);
            timelineBounds = timelineBounds.deflate({ left: this.styles.markerSize });
        }
        if (this.model.showEndMarker) {
            const endMarker = this.endMarker.calcProps(new geom.Size(this.styles.markerSize, this.styles.markerSize));
            endMarker.bounds = new geom.Rect(size.width - this.styles.markerSize, size.height / 2 - this.styles.markerSize / 2, this.styles.markerSize, this.styles.markerSize);
            timelineBounds = timelineBounds.deflate({ right: this.styles.markerSize });
        }

        // Add props for the timeline line
        this.timeline.createProps({
            path: [[timelineBounds.left, timelineBounds.height / 2], [timelineBounds.right, timelineBounds.height / 2 + 1]],
            layer: -1,
            bounds: timelineBounds
        });

        const annotationsBounds = timelineBounds.clone();
        const calcAnnotations = () => {
            const annotationProps = this.annotations.calcProps(annotationsBounds.size);
            annotationProps.bounds = annotationsBounds;
        };

        calcAnnotations();

        let needsRecalc = false;
        const centerV = annotationsBounds.height / 2;
        for (const annotationElement of this.annotations.itemElements) {
            const elementPostionY = annotationElement.model.y * annotationsBounds.size.height;
            if (annotationElement.bounds.top < centerV && annotationElement.bounds.bottom > centerV) {
                const moveToTop = (centerV - annotationElement.bounds.top) > (annotationElement.bounds.bottom - centerV);
                if (moveToTop) {
                    const elementOffset = Math.max(0, annotationElement.bounds.bottom - elementPostionY) / annotationsBounds.height;
                    annotationElement.model.y = Math.max(0, 0.45 - elementOffset);
                } else {
                    const elementOffset = Math.max(0, elementPostionY - annotationElement.bounds.top) / annotationsBounds.height;
                    annotationElement.model.y = Math.min(1 - annotationElement.bounds.height / annotationsBounds.height, 0.55 + elementOffset);
                }
                needsRecalc = true;
            } else if (annotationElement.bounds.top < 0) {
                annotationElement.model.y = 0;
            } else if ((annotationElement.bounds.bottom > annotationsBounds.height)) {
                annotationElement.model.y = Math.max(0, 1 - annotationElement.bounds.height / annotationsBounds.height);
            }
        }

        if (needsRecalc) {
            calcAnnotations();
        }

        return { size };
    }

    async distributeMilestones() {
        const sortedMilestones = _.cloneDeep(this.model.annotations.items).sort((a, b) => a.x - b.x).map(({ id }) => this.annotations.elements[id]);

        // First, distribute horizontally and remove hasBeenDragged
        sortedMilestones.forEach((milestone, idx) => {
            milestone.model.x = (1 / (sortedMilestones.length + 1)) * (idx + 1);
            milestone.model.y = idx % 2 === 0 ? 0.25 : 0.75;
            milestone.model.hasBeenDragged = false;
        });

        // Refresh to get fresh milestone bounds
        this.refreshElement(true);
        // Wait for animation
        await new Promise(resolve => setTimeout(resolve, 300));

        // Distribute vertically in case there are intersections
        const annotationsBounds = this.annotations.calculatedProps.bounds.zeroOffset();
        const getBounds = milestone => milestone.anchorBounds.union(milestone.text.calculatedProps.bounds.offset(milestone.calculatedProps.bounds.position));
        const getMaxHeight = milestones => milestones.reduce((maxHeight, milestone) => Math.max(getBounds(milestone).height, maxHeight), 0);
        const distributeRow = (milestones, availableBounds) => {
            const hasIntersections = milestones.reduce(
                (hasIntersections, milestone) =>
                    hasIntersections ||
                    milestones.filter(m => m !== milestone).some(m => getBounds(m).intersects(getBounds(milestone), true)),
                false
            );
            if (!hasIntersections) {
                return;
            }

            // Split milestones in two rows
            const topMilestones = milestones.filter((_, idx) => idx % 2 !== 0);
            const bottomMilestones = milestones.filter((_, idx) => idx % 2 === 0);

            const topMilestonesHeight = getMaxHeight(topMilestones);
            const bottomMilestonesHeight = getMaxHeight(bottomMilestones);

            const verticalPadding = Math.max(0, (availableBounds.height - topMilestonesHeight - bottomMilestonesHeight) / 3);

            const topBounds = availableBounds.removePadding({ top: verticalPadding, left: 0, bottom: verticalPadding * 2 + bottomMilestonesHeight, right: 0 });
            topMilestones.forEach(milestone => {
                milestone.model.y = topBounds.top / annotationsBounds.height;
            });

            const bottomBounds = availableBounds.removePadding({ top: verticalPadding * 2 + topMilestonesHeight, left: 0, bottom: verticalPadding, right: 0 });
            bottomMilestones.forEach(milestone => {
                milestone.model.y = bottomBounds.top / annotationsBounds.height;
            });
        };

        const topMilestones = sortedMilestones.filter(milestone => milestone.model.y < 0.5);
        distributeRow(topMilestones, annotationsBounds.removePadding({ top: 0, left: 0, bottom: this.annotations.bounds.height / 2, right: 0 }));

        const bottomMilestones = sortedMilestones.filter(milestone => milestone.model.y >= 0.5);
        distributeRow(bottomMilestones, annotationsBounds.removePadding({ top: this.annotations.bounds.height / 2, left: 0, bottom: 0, right: 0 }));
    }

    _migrate_10() {
        this.model.annotations = {
            items: this.model.items
                .map((milestoneModel, idx) => ({
                    ...this.getDefaultAnnotationModel(),
                    nodeType: this.model.showContent ? NodeType.CONTENT_AND_TEXT : NodeType.TEXT,
                    content_type: milestoneModel.content_type,
                    content_value: milestoneModel.content_value,
                    hasBeenDragged: !!milestoneModel.hasBeenDragged,
                    body: milestoneModel.body,
                    title: milestoneModel.title,
                    x: milestoneModel.x ?? idx / this.model.items.length,
                    y: milestoneModel.y
                }))
                .sort((a, b) => a.x - b.x)
                .map((milestoneModel, idx) => ({
                    ...milestoneModel,
                    y: _.defaultTo(milestoneModel.y, idx % 2 === 0 ? 0.25 : 0.75)
                })),
            connections: {
                items: []
            }
        };

        delete this.model.items;
    }
}

class TimelineSpans extends SVGPolylineElement {
    getAnchorPointType() {
        return geom.AnchorType.FREE;
    }

    getAnchorPoint(connector, anchor, connectorPoint, connectorType) {
        const verticalOffset = connectorPoint.y < this.calculatedProps.bounds.centerV ? 4 : -4;
        return new geom.Point(connectorPoint.x, this.calculatedProps.bounds.centerV);
    }
}

class TimelineMarker extends TextElement {
    get allowStyling() {
        return true;
    }

    get animateChildren() {
        return false;
    }

    _getAnimations() {
        return [];
    }
}

export const elements = {
    Timeline
};
